import { ReactNode, useId, useState } from 'react'

import './disclosureSection.scss'

export interface DisclosureSectionProps {
  /** Text shown on the reveal link (both when open and closed). */
  label: ReactNode
  /** Uncontrolled initial open state. Ignored when `open` is provided. */
  defaultOpen?: boolean
  /** Controlled open state. When provided, the parent owns the open/closed state. */
  open?: boolean
  onToggle?: (open: boolean) => void
  children: ReactNode
  className?: string
}

/**
 * A lightweight progressive-disclosure primitive: a reveal link that expands to
 * show its children. Supports both controlled (`open` + `onToggle`) and
 * uncontrolled (`defaultOpen`) usage. Used by the expense forms to hide
 * advanced fields until the member asks for them.
 */
export function DisclosureSection({
  label,
  defaultOpen,
  open,
  onToggle,
  children,
  className,
}: DisclosureSectionProps) {
  const [internalOpen, setInternalOpen] = useState(!!defaultOpen)
  const isControlled = typeof open === 'boolean'
  const isOpen = isControlled ? open : internalOpen
  const bodyId = useId()

  const toggle = () => {
    const next = !isOpen
    if (!isControlled) {
      setInternalOpen(next)
    }
    onToggle?.(next)
  }

  return (
    <div className={`disclosure-section col-12 ${className || ''}`}>
      <button
        type="button"
        className="disclosure-section__toggle btn btn-link p-0"
        aria-expanded={isOpen}
        aria-controls={bodyId}
        onClick={toggle}>
        <i className="material-icons disclosure-section__icon">
          {isOpen ? 'expand_less' : 'expand_more'}
        </i>
        <span>{label}</span>
      </button>
      {/* Always mounted (not conditionally rendered) so `aria-controls` above always
          references a real element, and so child form state survives a collapse. */}
      <div id={bodyId} className="disclosure-section__body" hidden={!isOpen}>
        {children}
      </div>
    </div>
  )
}
